import React, { createContext, useContext, useState, useCallback } from 'react';

const LoadingContext = createContext();

export const useLoading = () => {
    const context = useContext(LoadingContext);
    if (!context) {
        throw new Error('useLoading must be used within a LoadingProvider');
    }
    return context;
};

export const LoadingProvider = ({ children }) => {
    const [loadingSteps, setLoadingSteps] = useState({
        banners: false,
        merchants: false,
        categories: false,
        prefetch: false
    });

    const [progress, setProgress] = useState(0);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    // Calculate progress based on completed steps
    const calculateProgress = useCallback((steps) => {
        const weights = {
            banners: 20,      // 20% - Fast, small data
            merchants: 50,    // 50% - Initial 5 pages (main content)
            categories: 25,   // 25% - Product categories
            prefetch: 5       // 5% - Background task, user doesn't wait for this
        };

        let totalProgress = 0;
        Object.keys(steps).forEach(key => {
            if (steps[key]) {
                totalProgress += weights[key];
            }
        });

        return totalProgress;
    }, []);

    const markStepComplete = useCallback((step) => {
        setLoadingSteps(prev => {
            const newSteps = { ...prev, [step]: true };
            const newProgress = calculateProgress(newSteps);
            setProgress(newProgress);

            // Check if all steps are complete
            const allComplete = Object.values(newSteps).every(v => v === true);
            if (allComplete) {
                setIsInitialLoading(false);
            }

            return newSteps;
        });
    }, [calculateProgress]);

    const resetLoading = useCallback(() => {
        setLoadingSteps({
            banners: false,
            merchants: false,
            categories: false,
            prefetch: false
        });
        setProgress(0);
        setIsInitialLoading(true);
    }, []);

    return (
        <LoadingContext.Provider value={{
            loadingSteps,
            progress,
            isInitialLoading,
            markStepComplete,
            resetLoading
        }}>
            {children}
        </LoadingContext.Provider>
    );
};
